import {
  EyeIcon,
  PlusIcon,
  CircleXIcon,
  SendIcon,
  CircleCheckIcon,
  Undo2Icon,
  CoinsIcon,
  WalletIcon,
  RotateCcwIcon,
  MessageCircleIcon,
  ShieldAlertIcon,
  BanIcon,
  UserRoundCogIcon,
} from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import {
  ActionMenu,
  ActionMenuContent,
  ActionMenuItem,
  ActionMenuTrigger,
  MenuDotsVerticalIcon,
  MenuEditIcon,
  MenuTrashIcon,
} from '@noor/ui';

/** Shared trigger, fixed popup dimensions, and semantic action items. */
export function RowActionMenu({
  label,
  title,
  disabled = false,
  children,
}: {
  label: string;
  title?: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <ActionMenu>
      <ActionMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          title={title ?? label}
          disabled={disabled}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-muted text-foreground transition-colors outline-none hover:bg-muted/80 focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-muted/80"
        >
          <MenuDotsVerticalIcon aria-hidden="true" className="size-5" />
        </button>
      </ActionMenuTrigger>
      <ActionMenuContent
        align="end"
        side="bottom"
        className="z-[1600] w-60 max-w-[calc(100vw-2rem)]"
      >
        {children}
      </ActionMenuContent>
    </ActionMenu>
  );
}

const actionIcons = {
  edit: MenuEditIcon,
  view: EyeIcon,
  create: PlusIcon,
  delete: MenuTrashIcon,
  cancel: CircleXIcon,
  publish: SendIcon,
  finish: CircleCheckIcon,
  return: Undo2Icon,
  debt: CoinsIcon,
  payment: WalletIcon,
  reorder: RotateCcwIcon,
  chat: MessageCircleIcon,
  sanction: ShieldAlertIcon,
  block: BanIcon,
  manager: UserRoundCogIcon,
};

/** Screens choose an action and label; icon and item layout live here. */
export function RowActionMenuItem({
  action,
  children,
  ...props
}: Omit<ComponentProps<typeof ActionMenuItem>, 'className' | 'asChild'> & {
  action: keyof typeof actionIcons;
}) {
  const Icon = actionIcons[action];
  return (
    <ActionMenuItem {...props} className="min-h-11 gap-2 p-3 text-sm font-medium">
      <Icon aria-hidden="true" className="size-5" />
      <span className="min-w-0 whitespace-normal">{children}</span>
    </ActionMenuItem>
  );
}
